import React, { useMemo } from 'react';
import DecisionEngineBlock from '../../decision-engine/DecisionEngineBlock';
import {
  aksiyonKilidiHesapla,
  aksiyonSkoruHesapla,
  aksiyonlaraKilitUygula
} from '../../decision-engine/decisionEngineScoring';
import DrawerFooterActions from './DrawerFooterActions';
import { olusturAksiyonDurumKapsami } from '../../decision-engine/actionStateScope';

function Row({ label, value }) {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-black text-slate-900 dark:text-white">{value}</span>
    </li>
  );
}

export default function SegmentDetailDrawer({ entity, onClose }) {
  const segment = entity?.payload || {};
  const confidence = String(segment.confidence || 'LOW').toUpperCase();
  const confidenceLabel = confidence === 'HIGH' ? 'YÜKSEK' : confidence === 'MEDIUM' ? 'ORTA' : 'DÜŞÜK';
  const sampleSize = Number(segment.orneklemBoyutu || segment.sampleSize || segment.users || 0);
  const orneklemYeterli = typeof segment.orneklemYeterliMi === 'boolean' ? segment.orneklemYeterliMi : sampleSize >= 20;
  const uygunluk = String(segment.uygunluk || 'yalniz_icgoru').replaceAll('_', ' ');
  const tazelik = String(segment.tazelikDurumu || 'yaslanan').replaceAll('_', ' ');
  const segmentName = segment.name || 'Segment';

  const narrative = Number(segment.acceptPct || 0) < 30
    ? 'Kabul oranı düşük; segment için öneri başlığı ve ilk tarif sıralaması birlikte iyileştirilmeli.'
    : 'Kabul oranı güçlü; segmentte daha yüksek etki için pişirmeye giden adımlar kısaltılmalı.';

  const productMix = Array.isArray(segment.productMix) && segment.productMix.length > 0
    ? segment.productMix
    : [
      { label: 'Alakart', pct: Math.max(8, Math.min(72, Math.round(Number(segment.acceptPct || 0) + 14))) },
      { label: 'Diyet', pct: Math.max(5, Math.min(60, Math.round(Number(segment.activityPct || 0) * 0.52))) },
      { label: 'Tatlı', pct: Math.max(3, Math.min(44, Math.round(Number(segment.wowPct || 0) + 8))) }
    ];

  const decisionLayer = useMemo(() => {
    const anomalies = [];
    if (Number(segment.acceptPct || 0) > 0 && Number(segment.activityPct || 0) === 0) {
      anomalies.push({
        title: 'Aktiflik 0 iken kabul oranı var',
        meaning: 'Segmentte aktif kullanıcı görünmüyor ancak kabul sinyali üretilmiş.',
        risk: 'Olay eşlemesi veya segment filtresi uyumsuz olabilir.',
        recommendation: 'Segment filtresi ve kabul olay sözleşmesi birlikte doğrulanmalı.'
      });
    }
    if (Number(segment.wowPct || 0) > Number(segment.acceptPct || 0)) {
      anomalies.push({
        title: 'Wow oranı kabul oranını aşıyor',
        meaning: 'Alt sinyal üst sinyalden büyük görünüyor.',
        risk: 'Sinyal zinciri veya oran hesaplamasında tutarsızlık olabilir.',
        recommendation: 'Kabul/wow oran üretimi aynı örneklemde tekrar hesaplanmalı.'
      });
    }

    const { actionLock, actionLockReason } = aksiyonKilidiHesapla({
      anomaliSayisi: anomalies.length,
      guvenBandi: confidenceLabel,
      dusukOrneklemKilidi: !orneklemYeterli,
      anomaliMesaji: 'Bu aksiyon şu an uygulanamaz: veri tutarsızlığı giderilmeden canlı aksiyon açılmamalı.',
      dusukOrneklemMesaji: 'Bu aksiyon şu an uygulanamaz: örneklem düşük, önce gözlem modunda izlenmeli.'
    });

    const actions = [
      {
        actionKey: 'ilk3-niyet-uyumu',
        title: `${segmentName} için ilk 3 öneride niyet uyumlu tarifleri artır`,
        reason: 'Kabul sinyali var ancak tamamlama gücü istenen seviyede değil.',
        doNotApplyWhen: 'Örneklem düşükse veya olay tutarsızlığı varsa canlıya alınmamalı.',
        expectedImpact: 'Kabul ve pişirme hattında kontrollü artış',
        affectedSegment: segmentName,
        score: aksiyonSkoruHesapla(7, confidenceLabel, sampleSize, { yuksek: 100, orta: 20 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%4 Kabul oranı', '+%2 Pişirme oranı', '+90 kullanıcı etki alanı'],
        baselineExpected: {
          metric: 'Kabul oranı',
          baseline: `%${Number(segment.acceptPct || 0).toLocaleString('tr-TR')}`,
          expected: `%${Number((Number(segment.acceptPct || 0) + 4).toFixed(2)).toLocaleString('tr-TR')}`
        },
        context: {
          orneklem: sampleSize,
          segment: segmentName,
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          'Kabul var, ancak wow ve pişirme etkisi sınırlı.',
          'İlk öneri blokları segment niyeti ile daha güçlü hizalanmalı.'
        ]
      },
      {
        actionKey: 'gorsel-baslik-varyasyon',
        title: 'Düşük performanslı tariflerde görsel ve başlık varyasyonu dene',
        reason: 'Segmentte seçim anı sürtünmesi yüksek olabilir.',
        doNotApplyWhen: 'Aynı dönemde veri akışında tutarsız alarmı varsa test ertelenmeli.',
        expectedImpact: 'Görüntüleme -> seçim geçişinde artış',
        affectedSegment: segmentName,
        score: aksiyonSkoruHesapla(5, confidenceLabel, sampleSize, { yuksek: 100, orta: 20 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%3 Seçim oranı', '+%2 Plan oranı'],
        baselineExpected: {
          metric: 'Aktiflik',
          baseline: `%${Number(segment.activityPct || 0).toLocaleString('tr-TR')}`,
          expected: `%${Number((Number(segment.activityPct || 0) + 3).toFixed(2)).toLocaleString('tr-TR')}`
        },
        context: {
          orneklem: sampleSize,
          segment: segmentName,
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'testte',
        resultDelta: '+%1.2 seçim',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['Seçim oranı aktiviteye göre düşük kalıyor.', 'Varyasyon testi düşük riskle hızlı sinyal üretir.']
      },
      {
        actionKey: 'wow-gorunurluk',
        title: 'Yüksek wow sinyalli tarifleri bu segmente daha sık göster',
        reason: 'Wow sinyali yüksek içeriklerin görünürlüğü artarsa etki büyür.',
        doNotApplyWhen: 'Wow oranı tutarsızlık alarmı veriyorsa önce hesap doğrulanmalı.',
        expectedImpact: 'Wow ve pişirme etkisinde artış',
        affectedSegment: segmentName,
        score: aksiyonSkoruHesapla(4, confidenceLabel, sampleSize, { yuksek: 100, orta: 20 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%2 Wow oranı', '+%1.5 Pişirme oranı'],
        baselineExpected: {
          metric: 'Wow oranı',
          baseline: `%${Number(segment.wowPct || 0).toLocaleString('tr-TR')}`,
          expected: `%${Number((Number(segment.wowPct || 0) + 2).toFixed(2)).toLocaleString('tr-TR')}`
        },
        context: {
          orneklem: sampleSize,
          segment: segmentName,
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['Yüksek wow içeriği karar kalitesini yükseltir.', 'Segmentte tekrar etkileşim olasılığı artar.']
      }
    ];

    const actionsWithLock = aksiyonlaraKilitUygula(actions, actionLock, actionLockReason);
    const priority = Number(segment.priorityScore || 0) >= 70
      ? 'HIGH'
      : Number(segment.priorityScore || 0) >= 40
        ? 'MEDIUM'
        : (segment.priority || 'LOW');

    return {
      narrative,
      keyTakeaway: `${segmentName} segmentinde en yüksek etki, ilk öneri kalitesini iyileştirme aksiyonundan geliyor.`,
      actions: actionsWithLock,
      anomalies,
      priority,
      confidence: confidenceLabel,
      sampleSize,
      expectedOutcome: `${segmentName} için kabul oranında +2 ila +4 puan iyileşme hedeflenir.`,
      actionLock,
      actionLockReason
    };
  }, [
    confidenceLabel,
    sampleSize,
    segment,
    segmentName,
    orneklemYeterli,
    narrative
  ]);

  const actionStateScope = olusturAksiyonDurumKapsami('segment', entity);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(segment, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `segment-detay-${segment.key || Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <DecisionEngineBlock
        narrative={decisionLayer.narrative}
        keyTakeaway={decisionLayer.keyTakeaway}
        actions={decisionLayer.actions}
        anomalies={decisionLayer.anomalies}
        priority={decisionLayer.priority}
        confidence={decisionLayer.confidence}
        sampleSize={decisionLayer.sampleSize}
        expectedOutcome={decisionLayer.expectedOutcome}
        actionLock={decisionLayer.actionLock}
        actionLockReason={decisionLayer.actionLockReason}
        actionStateScope={actionStateScope}
      />

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs text-slate-500 dark:text-slate-400">Segment Özet Alanı</p>
        <h4 className="mt-1 text-lg font-black text-slate-900 dark:text-white">{segmentName}</h4>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Kullanıcı: {Number(segment.users || 0).toLocaleString('tr-TR')} | Güven: {confidenceLabel} | Örneklem: {sampleSize.toLocaleString('tr-TR')}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Uygunluk: {uygunluk} | Tazelik: {tazelik} | {orneklemYeterli ? 'Örneklem yeterli' : 'Örneklem düşük'}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Eğilim</p>
        <p className={`mt-1 text-sm font-bold ${Number(segment.trendPct || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {Number(segment.trendPct || 0) >= 0 ? '+' : ''}{Number(segment.trendPct || 0).toLocaleString('tr-TR')}%
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Davranış Kırılımı</p>
        <ul className="mt-2 space-y-1.5">
          <Row label="Aktiflik" value={`%${Number(segment.activityPct || 0).toLocaleString('tr-TR')}`} />
          <Row label="Kabul" value={`%${Number(segment.acceptPct || 0).toLocaleString('tr-TR')}`} />
          <Row label="Wow" value={`%${Number(segment.wowPct || 0).toLocaleString('tr-TR')}`} />
        </ul>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Ürün / Yüzey Dağılımı</p>
        <ul className="mt-2 space-y-1.5">
          {productMix.map((item) => (
            <Row key={item.label} label={item.label} value={`%${Number(item.pct || 0).toLocaleString('tr-TR')}`} />
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Liste</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Bu segmentin kullanıcı listesi ve ham detayları gerçek segment uç noktası ile doldurulur.</p>
      </section>

      <DrawerFooterActions onClose={onClose} onExport={handleExport} />
    </div>
  );
}


